import { useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { CenteredContainer, ErrorCard } from './ErrorPage.styles';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/react';
import { isChunkLoadError } from 'utils/isChunkLoadError';

export const ErrorPage = () => {
  const { t } = useTranslation();
  const error = useRouteError();

  if (error instanceof Error && !isChunkLoadError(error)) {
    Sentry.captureException(error);
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('Route error:', error);
  }

  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : '';
  const fullErrorDetails = `${errorMessage}\n\n${errorStack}`;

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullErrorDetails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = fullErrorDetails;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <CenteredContainer>
      <ErrorCard elevation={3}>
        <SentimentVeryDissatisfiedIcon color="action" sx={{ fontSize: 80, opacity: 0.6 }} />
        <div>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            {t('common.errorPage.somethingWentWrong')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
            {t('common.errorPage.tryAgainLater')}
          </Typography>
        </div>
        {process.env.NODE_ENV !== 'production' && (
          <Button
            variant="text"
            size="small"
            onClick={handleCopy}
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            color={copied ? 'success' : 'primary'}>
            {copied ? t('common.errorPage.copied') : t('common.errorPage.copyFullStackTrace')}
          </Button>
        )}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button variant="contained" onClick={handleReload} size="large">
            {t('common.errorPage.reloadPage')}
          </Button>
          <Button variant="outlined" onClick={handleGoHome} size="large">
            {t('common.errorPage.goHome')}
          </Button>
        </div>
      </ErrorCard>
    </CenteredContainer>
  );
};
